import React, { useEffect, useMemo, useState } from 'react';
import axios from '../axios';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import StatusBadge from '../components/StatusBadge';
import LeadProgressBar from '../components/LeadProgressBar';
import MobileDataCard from '../components/MobileDataCard';
import { formatDate } from '../utils/formatters';

interface Company {
  id: string;
  name: string;
  domain?: string;
  status: string;
  autoRejectNewLeads: boolean;
}

interface Lead {
  id: string;
  companyName: string;
  domain: string;
  status: 'SUBMITTED' | 'ACCEPTED' | 'REJECTED' | 'DELETED';
  companyStatus?: string;
  dealStatus?: 'OPEN' | 'QUALIFIED' | 'OUTREACH_STARTED' | 'MEETING_SCHEDULED' | 'PROPOSAL_SENT' | 'IN_NEGOTIATION' | 'CLOSED_WON' | 'CLOSED_LOST' | null;
  contactSource: 'PERSONAL' | 'BUSINESS';
  createdAt: string;
}

const Companies: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [leadSort, setLeadSort] = useState<'status' | 'newest' | 'oldest'>('status');
  const [companyQuery, setCompanyQuery] = useState('');

  const fetchCompanies = async () => {
    if (user?.role === 'PARTNER') {
      const response = await axios.get('/api/partner/leads');
      setLeads(response.data);
      return;
    }

    const response = await axios.get('/api/partner/companies');
    setCompanies(response.data);
  };

  useEffect(() => {
    fetchCompanies();
  }, [user?.role]);

  const sortedLeads = useMemo(() => {
    const statusOrder: Record<string, number> = {
      SUBMITTED: 0,
      ACCEPTED: 1,
      REJECTED: 98,
      DELETED: 99,
    };
    const dealOrder: Record<string, number> = {
      OPEN: 1,
      QUALIFIED: 2,
      OUTREACH_STARTED: 3,
      MEETING_SCHEDULED: 4,
      PROPOSAL_SENT: 5,
      IN_NEGOTIATION: 6,
      CLOSED_WON: 7,
      CLOSED_LOST: 97,
    };

    return [...leads].sort((a, b) => {
      if (leadSort === 'newest') {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      }
      if (leadSort === 'oldest') {
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      }

      const aOrder = a.dealStatus ? (dealOrder[a.dealStatus] ?? 50) : (statusOrder[a.status] ?? 50);
      const bOrder = b.dealStatus ? (dealOrder[b.dealStatus] ?? 50) : (statusOrder[b.status] ?? 50);
      const statusDiff = aOrder - bOrder;
      if (statusDiff !== 0) {
        return statusDiff;
      }
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [leadSort, leads]);

  const filteredCompanies = useMemo(() => {
    const normalizedQuery = companyQuery.trim().toLowerCase();
    if (!normalizedQuery) {
      return companies;
    }

    return companies.filter((company) => {
      const policyLabel = company.autoRejectNewLeads ? t('companies.autoReject') : t('companies.accepting');
      return [
        company.name,
        company.domain || '',
        t(`status.company.${company.status}`, company.status),
        policyLabel,
      ].some((value) => value.toLowerCase().includes(normalizedQuery));
    });
  }, [companies, companyQuery, t]);

  if (user?.role === 'PARTNER') {
    const getPartnerBadge = (lead: Lead) => {
      if (lead.dealStatus) {
        return <StatusBadge kind="deal" status={lead.dealStatus} labelKeyPrefix="partnerStatus.deal" />;
      }
      return <StatusBadge kind="lead" status={lead.status} labelKeyPrefix="partnerStatus.lead" />;
    };

    return (
      <section className="mx-auto">
        <h1 className="text-2xl md:text-3xl font-bold mb-6 text-[#111D4E]">{t('companies.partnerTitle')}</h1>
        <div className="bg-white/90 text-gray-800 rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4 bg-gradient-to-r from-[#E9F4FB] via-white to-[#F4F8FB]">
            <div>
              <div className="text-sm font-semibold text-[#111D4E]">{t('companies.partnerTitle')}</div>
              <div className="text-xs text-gray-600">{t('companies.partnerSubtitle')}</div>
            </div>
            <div className="flex gap-2">
              <select
                value={leadSort}
                onChange={(e) => setLeadSort(e.target.value as 'status' | 'newest' | 'oldest')}
                className="px-3 py-2 border border-slate-200 rounded-md bg-white text-sm"
              >
                <option value="status">{t('companies.sort.status')}</option>
                <option value="newest">{t('companies.sort.newest')}</option>
                <option value="oldest">{t('companies.sort.oldest')}</option>
              </select>
              <button
                onClick={fetchCompanies}
                className="px-4 py-2 bg-[#47ABDB] text-white rounded-md text-sm font-semibold shadow-sm hover:bg-[#3d8fb8]"
              >
                {t('common.refresh')}
              </button>
            </div>
          </div>
          <div className="space-y-3 p-4 md:hidden">
            {sortedLeads.length === 0 ? (
              <div className="rounded-2xl border border-slate-200/80 bg-white px-5 py-10 text-center text-base text-gray-500">
                {t('companies.partnerEmpty')}
              </div>
            ) : (
              sortedLeads.map((lead) => (
                <MobileDataCard
                  key={lead.id}
                  title={lead.companyName}
                  badge={getPartnerBadge(lead)}
                  subtitle={lead.domain}
                  rows={[
                    { label: t('submitLead.contactSource'), value: t(`submitLead.contactSourceValue.${lead.contactSource}`) },
                    { label: t('companies.submittedAt'), value: formatDate(lead.createdAt, i18n.resolvedLanguage) },
                  ]}
                  actions={<LeadProgressBar leadStatus={lead.status} dealStatus={lead.dealStatus} compact />}
                />
              ))
            )}
          </div>
          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-full text-left table-auto text-[15px] md:text-base">
              <thead className="bg-[#F3F6F9] text-sm uppercase tracking-wide text-[#111D4E]">
                <tr>
                  <th className="px-5 py-3 font-semibold">{t('submitLead.company')}</th>
                  <th className="px-5 py-3 font-semibold">{t('submitLead.domain')}</th>
                  <th className="px-5 py-3 font-semibold">{t('submitLead.contactSource')}</th>
                  <th className="px-5 py-3 font-semibold">{t('companies.progress')}</th>
                  <th className="px-5 py-3 font-semibold">{t('companies.submittedAt')}</th>
                </tr>
              </thead>
              <tbody>
                {sortedLeads.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-5 py-10 text-center text-base text-gray-500">
                      {t('companies.partnerEmpty')}
                    </td>
                  </tr>
                ) : (
                  sortedLeads.map((lead, idx) => (
                    <tr key={lead.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-[#FBFCFE]'}>
                      <td className="px-5 py-4 font-medium text-[#111D4E]">{lead.companyName}</td>
                      <td className="px-5 py-4 text-sm">{lead.domain}</td>
                      <td className="px-5 py-4 text-sm">{t(`submitLead.contactSourceValue.${lead.contactSource}`)}</td>
                      <td className="px-5 py-4 min-w-[24rem]"><LeadProgressBar leadStatus={lead.status} dealStatus={lead.dealStatus} /></td>
                      <td className="px-5 py-4">{formatDate(lead.createdAt, i18n.resolvedLanguage)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="mx-auto">
      <h1 className="text-2xl md:text-3xl font-bold mb-6 text-[#111D4E]">{t('companies.title')}</h1>
      <div className="bg-white/90 text-gray-800 rounded-2xl shadow-sm border border-slate-200/80 overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-5 py-4 bg-gradient-to-r from-[#E9F4FB] via-white to-[#F4F8FB]">
          <div>
            <div className="text-sm font-semibold text-[#111D4E]">{t('companies.title')}</div>
            <div className="text-xs text-gray-600">{t('companies.subtitle')}</div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <input
              value={companyQuery}
              onChange={(e) => setCompanyQuery(e.target.value)}
              placeholder={t('companies.searchPlaceholder')}
              className="w-full sm:w-auto sm:min-w-56 px-3 py-2 border border-slate-200 rounded-md bg-white text-sm"
            />
            <button
              onClick={fetchCompanies}
              className="px-4 py-2 bg-[#47ABDB] text-white rounded-md text-sm font-semibold shadow-sm hover:bg-[#3d8fb8]"
            >
              {t('common.refresh')}
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <div className="space-y-3 p-4 md:hidden">
            {filteredCompanies.length === 0 ? (
              <div className="rounded-2xl border border-slate-200/80 bg-white px-5 py-10 text-center text-base text-gray-500">
                {companyQuery.trim() ? t('companies.searchEmpty') : t('companies.empty')}
              </div>
            ) : (
              filteredCompanies.map((company) => (
                <MobileDataCard
                  key={company.id}
                  title={company.name}
                  badge={<StatusBadge kind="company" status={company.status} />}
                  subtitle={company.domain || '-'}
                  rows={[
                    { label: t('companies.leadPolicy'), value: company.autoRejectNewLeads ? t('companies.autoReject') : t('companies.accepting') },
                  ]}
                />
              ))
            )}
          </div>
          <div className="hidden overflow-x-auto md:block">
          <table className="min-w-full text-left table-auto text-[15px] md:text-base">
            <thead className="bg-[#F3F6F9] text-sm uppercase tracking-wide text-[#111D4E]">
              <tr>
                <th className="px-5 py-3 font-semibold">{t('submitLead.company')}</th>
                <th className="px-5 py-3 font-semibold">{t('submitLead.domain')}</th>
                <th className="px-5 py-3 font-semibold">{t('companies.status')}</th>
                <th className="px-5 py-3 font-semibold">{t('companies.leadPolicy')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredCompanies.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-5 py-10 text-center text-base text-gray-500">
                    {companyQuery.trim() ? t('companies.searchEmpty') : t('companies.empty')}
                  </td>
                </tr>
              ) : (
                filteredCompanies.map((company, idx) => (
                  <tr key={company.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-[#FBFCFE]'}>
                    <td className="px-5 py-4 font-medium text-[#111D4E]">{company.name}</td>
                    <td className="px-5 py-4 text-sm">{company.domain || '-'}</td>
                    <td className="px-5 py-4">
                      <StatusBadge kind="company" status={company.status} />
                    </td>
                    <td className="px-5 py-4 text-sm">{company.autoRejectNewLeads ? t('companies.autoReject') : t('companies.accepting')}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        </div>
      </div>
    </section>
  );
};

export default Companies;
